/**
 * API client (P30 / F).
 *
 * Auth chain in order of preference:
 * Auth: Google ID token from Stanford SSO (held in localStorage by
 * AuthContext). The portal allowlist gate verifies the user's email
 * is registered before any /api/v1/* call succeeds.
 */

// Same-origin: nginx reverse-proxies /api/v1/*, /metrics, /readyz, /healthz
// to the control-plane. The SPA and API live behind the same hostname.
// Set VITE_CONTROL_PLANE_URL only if you're running the SPA outside the
// gateway box (rare).
const BASE_URL = (import.meta.env?.VITE_CONTROL_PLANE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    public readonly url: string,
    /** Correlation ID — set if the request sent one or the server echoed one back. */
    public readonly correlationId?: string,
  ) {
    super(`API ${status} ${url}`);
    this.name = 'ApiError';
  }
}

/** Generate a short correlation ID (CRYPTO_RANDOM-ish for browser context). */
function newCorrelationId(): string {
  const bytes = new Uint8Array(8);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

interface FetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  const correlationId = newCorrelationId();
  const headers: Record<string, string> = {
    accept: 'application/json',
    'x-correlation-id': correlationId,
  };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  // Auth: server-issued portal session cookie, sent automatically by
  // the browser via `credentials: 'include'`. We deliberately do NOT
  // send the Google ID token as a Bearer header anymore — auth-fastify
  // rejects expired bearers with 401 BEFORE the cookie hook gets a
  // chance to run, which would lock the user out of an otherwise valid
  // session. The cookie alone is the source of truth.

  const response = await fetch(url, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    // Send the HttpOnly portal session cookie. Same-origin requests so
    // 'same-origin' would also work, but 'include' is explicit + future-
    // proof if we ever serve the SPA from a different origin.
    credentials: 'include',
  });

  // The control-plane echoes the inbound X-Correlation-Id back when it
  // can; otherwise we use the one we generated.
  const echoedCorr = response.headers.get('x-correlation-id') ?? correlationId;

  if (!response.ok) {
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = await response.text();
    }
    throw new ApiError(response.status, body, url, echoedCorr);
  }

  // Handle 204 + empty bodies
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}

/** Convenience: build a query-string from a plain object. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}
